import { ReactNode } from 'react';

import {
  Card,
  CardContent,
  CardProps,
  Divider,
  LinearProgress,
  Typography,
  makeStyles,
} from '@material-ui/core';

import { MeasurementConfig } from '../../helpers/time-window';
import { SimplifiedQuery } from './SimplifiedQuery';
import {
  MeasurementInput,
  isAverageMeasurement,
  isRatioMeasurement,
} from './snippets';

const useStyles = makeStyles(theme => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));

export type QueryPreviewCardProps = CardProps & {
  entity?: string | null;
  factTable?: string | null;
  measurement?: MeasurementInput;
  metricWindow?: MeasurementConfig;
  dimMeasurement?: boolean;
  title?: string;
  description?: string;
  loading?: boolean;
  empty?: ReactNode;
};

export const QueryPreviewCard = ({
  entity,
  factTable,
  measurement,
  metricWindow,
  dimMeasurement,
  title,
  description,
  loading,
  empty,
  ...rest
}: QueryPreviewCardProps) => {
  const classes = useStyles();

  if (loading) {
    return (
      <Card {...rest}>
        <CardContent>
          <LinearProgress />
        </CardContent>
      </Card>
    );
  }

  const hasQuery =
    measurement &&
    (isAverageMeasurement(measurement) || isRatioMeasurement(measurement));

  if (!hasQuery) {
    if (empty) {
      return (
        <Card {...rest}>
          <CardContent>{empty}</CardContent>
        </Card>
      );
    }
    return null;
  }

  const hasHeader = !!(title || description);

  return (
    <Card {...rest}>
      <CardContent className={classes.content}>
        {hasHeader && (
          <>
            {title && <Typography variant="h6">{title}</Typography>}
            {description && (
              <Typography variant="body2" color="textSecondary">
                {description}
              </Typography>
            )}
            <Divider />
          </>
        )}
        <SimplifiedQuery
          entity={entity}
          factTable={factTable}
          measurement={measurement}
          metricWindow={metricWindow}
          dimMeasurement={dimMeasurement}
        />
      </CardContent>
    </Card>
  );
};
